import { Link } from "react-router-dom";
import {
  Plug,
  MessageCircle,
  Calendar,
  Video,
  CreditCard,
  Printer,
  Bot,
  Bell,
  ArrowRight,
  ArrowLeftRight,
  ArrowDownToLine,
  ArrowUpFromLine,
} from "lucide-react";
import { useLang } from "@/contexts/LangContext";

type Direction = "in" | "out" | "both";

type Integration = {
  icon: React.ComponentType<{ className?: string }>;
  en: string;
  bn: string;
  categoryEn: string;
  categoryBn: string;
  direction: Direction;
  dataEn: string;
  dataBn: string;
  accent: string; // tailwind text color
  bg: string; // tailwind bg tint
};

const INTEGRATIONS: Integration[] = [
  {
    icon: MessageCircle,
    en: "WhatsApp Business",
    bn: "WhatsApp Business",
    categoryEn: "Messaging",
    categoryBn: "মেসেজিং",
    direction: "both",
    dataEn: "Appointment reminders, Rx delivery, follow-up nudges, patient replies routed to the AI agent.",
    dataBn: "অ্যাপয়েন্টমেন্ট রিমাইন্ডার, Rx ডেলিভারি, ফলোআপ, রোগীর উত্তর AI এজেন্টে।",
    accent: "text-[#25D366]",
    bg: "bg-[#25D366]/10 border-[#25D366]/30",
  },
  {
    icon: CreditCard,
    en: "bKash · Nagad · Card",
    bn: "bKash · Nagad · কার্ড",
    categoryEn: "Payments",
    categoryBn: "পেমেন্ট",
    direction: "in",
    dataEn: "Invoice payment links via SSLCommerz. Confirmed payments post to the Daily Income Register automatically.",
    dataBn: "SSLCommerz-এর মাধ্যমে ইনভয়েস লিংক। নিশ্চিত পেমেন্ট দৈনিক হিসাব খাতায় যায়।",
    accent: "text-[#E2136E]",
    bg: "bg-[#E2136E]/10 border-[#E2136E]/25",
  },
  {
    icon: Calendar,
    en: "Google Calendar",
    bn: "Google ক্যালেন্ডার",
    categoryEn: "Scheduling",
    categoryBn: "শিডিউলিং",
    direction: "both",
    dataEn: "Two-way sync of chamber sessions and appointments. Cancellations reflect on both sides.",
    dataBn: "চেম্বার সেশন ও অ্যাপয়েন্টমেন্টের দ্বিমুখী সিঙ্ক। বাতিলও দুই দিকে দেখায়।",
    accent: "text-[#4285F4]",
    bg: "bg-[#4285F4]/10 border-[#4285F4]/25",
  },
  {
    icon: Video,
    en: "Daily.co video",
    bn: "Daily.co ভিডিও",
    categoryEn: "Teleconsult",
    categoryBn: "টেলিকনসাল্ট",
    direction: "both",
    dataEn: "One-tap video rooms attached to appointments; session start/end timestamps captured for billing.",
    dataBn: "অ্যাপয়েন্টমেন্টে সংযুক্ত ভিডিও রুম; বিলিং-এর জন্য টাইমস্ট্যাম্প রেকর্ড।",
    accent: "text-[#1BEBB9]",
    bg: "bg-[#1BEBB9]/10 border-[#1BEBB9]/30",
  },
  {
    icon: Bot,
    en: "Lovable AI (Gemini)",
    bn: "Lovable AI (Gemini)",
    categoryEn: "AI",
    categoryBn: "AI",
    direction: "both",
    dataEn: "SOAP notes, diagnosis suggestions, drug interactions, care-gap drafts — all in Bengali and English.",
    dataBn: "SOAP নোট, ডায়াগনোসিস, ড্রাগ ইন্টার‌্যাকশন, কেয়ার-গ্যাপ ড্রাফট — বাংলা ও ইংরেজিতে।",
    accent: "text-[#8b5cf6]",
    bg: "bg-[#8b5cf6]/10 border-[#8b5cf6]/25",
  },
  {
    icon: Printer,
    en: "PDF · Print · Email",
    bn: "PDF · প্রিন্ট · ইমেইল",
    categoryEn: "Documents",
    categoryBn: "ডকুমেন্ট",
    direction: "out",
    dataEn: "Bilingual prescriptions, invoices, medical certificates, referral letters — print-ready or emailed.",
    dataBn: "দ্বিভাষিক Rx, ইনভয়েস, মেডিকেল সার্টিফিকেট, রেফারেল — প্রিন্ট বা ইমেইল।",
    accent: "text-[#0f766e]",
    bg: "bg-[#0f766e]/10 border-[#0f766e]/25",
  },
  {
    icon: Bell,
    en: "Web Push · Desktop tray",
    bn: "ওয়েব পুশ · ডেস্কটপ",
    categoryEn: "Notifications",
    categoryBn: "নোটিফিকেশন",
    direction: "out",
    dataEn: "Native browser and Electron tray alerts for new bookings, sync failures, and follow-ups due.",
    dataBn: "নতুন বুকিং, সিঙ্ক ব্যর্থতা ও ফলোআপের জন্য ব্রাউজার/ডেস্কটপ অ্যালার্ট।",
    accent: "text-[#f59e0b]",
    bg: "bg-[#f59e0b]/10 border-[#f59e0b]/25",
  },
  {
    icon: ArrowDownToLine,
    en: "CSV · Data export",
    bn: "CSV · ডেটা এক্সপোর্ট",
    categoryEn: "Export",
    categoryBn: "এক্সপোর্ট",
    direction: "out",
    dataEn: "One-click export of patients, prescriptions, invoices, and audit logs. Your data, no lock-in.",
    dataBn: "রোগী, Rx, ইনভয়েস ও অডিট লগ এক-ক্লিকে এক্সপোর্ট। কোনো লক-ইন নেই।",
    accent: "text-[#1ba179]",
    bg: "bg-[#1ba179]/10 border-[#1ba179]/25",
  },
];

const DirectionBadge = ({ dir, t }: { dir: Direction; t: <T,>(en: T, bn: T) => T }) => {
  const map = {
    in: { icon: ArrowDownToLine, en: "Inbound", bn: "ইনবাউন্ড" },
    out: { icon: ArrowUpFromLine, en: "Outbound", bn: "আউটবাউন্ড" },
    both: { icon: ArrowLeftRight, en: "Two-way", bn: "দ্বিমুখী" },
  } as const;
  const { icon: Icon, en, bn } = map[dir];
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#475569] bg-[#f1f5f9] border border-[#e2e8f0] px-2 py-0.5 rounded-full">
      <Icon className="w-3 h-3" />
      {t(en, bn)}
    </span>
  );
};

export default function IntegrationsSection() {
  const { t, lang } = useLang();
  const displayFont =
    lang === "bn"
      ? "'Noto Sans Bengali', 'Plus Jakarta Sans', system-ui, sans-serif"
      : "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif";

  return (
    <section
      aria-label={t("Integrations and data flow", "ইন্টিগ্রেশন ও ডেটা ফ্লো")}
      className="border-t border-[#e2e8f0] bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.24em] text-[#1ba179] bg-[#e6faf3] border border-[#c7f1e2] px-3 py-1.5 rounded-full mb-4">
            <Plug className="w-3.5 h-3.5" />
            {t("Integrations", "ইন্টিগ্রেশন")}
          </div>
          <h2
            className="text-3xl sm:text-4xl font-bold text-[#0c2340] leading-tight"
            style={{ fontFamily: displayFont }}
          >
            {t(
              "Every tool your practice already uses — connected.",
              "আপনার প্র্যাকটিসে ব্যবহৃত সব টুল — যুক্ত।",
            )}
          </h2>
          <p className="mt-3 text-[15px] text-[#475569] leading-relaxed">
            {t(
              "Messaging, payments, calendar, video, AI, and documents — all flowing in and out of one patient record.",
              "মেসেজিং, পেমেন্ট, ক্যালেন্ডার, ভিডিও, AI ও ডকুমেন্ট — সব একটি রোগী রেকর্ডে যুক্ত।",
            )}
          </p>
        </div>

        {/* Data-flow diagram */}
        <div className="rounded-3xl border border-[#e2e8f0] bg-gradient-to-br from-[#f8fafc] to-white p-6 sm:p-8 mb-10">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[140px] text-center">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#64748b] mb-2">
                {t("Sources", "সোর্স")}
              </div>
              <div className="flex justify-center gap-2 flex-wrap">
                {INTEGRATIONS.filter((i) => i.direction !== "out").slice(0, 4).map((i) => (
                  <span key={i.en} className={`w-9 h-9 rounded-lg border flex items-center justify-center ${i.bg}`}>
                    <i.icon className={`w-4 h-4 ${i.accent}`} />
                  </span>
                ))}
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-[#94a3b8] shrink-0" />
            <div className="text-center px-4 py-3 rounded-2xl bg-gradient-to-br from-[#0c2340] to-[#1a4a6e] text-white min-w-[160px]">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#5cbdb9]">
                {t("DoctorsX core", "DoctorsX কোর")}
              </div>
              <div className="text-sm font-bold mt-1">
                {t("Patient record", "রোগী রেকর্ড")}
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-[#94a3b8] shrink-0" />
            <div className="flex-1 min-w-[140px] text-center">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#64748b] mb-2">
                {t("Destinations", "গন্তব্য")}
              </div>
              <div className="flex justify-center gap-2 flex-wrap">
                {INTEGRATIONS.filter((i) => i.direction !== "in").slice(0, 4).map((i) => (
                  <span key={i.en} className={`w-9 h-9 rounded-lg border flex items-center justify-center ${i.bg}`}>
                    <i.icon className={`w-4 h-4 ${i.accent}`} />
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Integration cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {INTEGRATIONS.map((i) => (
            <div
              key={i.en}
              className="rounded-2xl border border-[#e2e8f0] bg-white p-5 hover:border-[#1ba179] hover:-translate-y-0.5 transition"
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <span className={`w-10 h-10 rounded-xl border flex items-center justify-center ${i.bg}`}>
                  <i.icon className={`w-5 h-5 ${i.accent}`} />
                </span>
                <DirectionBadge dir={i.direction} t={t} />
              </div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#64748b]">
                {t(i.categoryEn, i.categoryBn)}
              </div>
              <div
                className="mt-0.5 text-[15px] font-bold text-[#0c2340]"
                style={{ fontFamily: displayFont }}
              >
                {t(i.en, i.bn)}
              </div>
              <p className="mt-2 text-[12.5px] text-[#64748b] leading-relaxed">
                {t(i.dataEn, i.dataBn)}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-dashed border-[#cbd5e1] bg-[#f8fafc] px-5 py-4">
          <p className="text-sm text-[#475569]">
            {t(
              "Missing something? Webhooks and CSV import cover most custom flows — talk to us.",
              "কিছু মিসিং? Webhook ও CSV ইম্পোর্ট বেশিরভাগ কাস্টম ফ্লো কভার করে — আমাদের জানান।",
            )}
          </p>
          <Link
            to="/features"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#1ba179] hover:underline"
          >
            {t("Explore all features", "সব ফিচার দেখুন")}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
